import { useQuery } from '@tanstack/react-query';
import React, { useEffect } from 'react';
import { useOutletContext, useParams } from 'react-router-dom';

import { Chip } from '@column/column-ui-kit';

import { ROUTE } from '~/app/routes';
import { CopyInput, Section } from '~/components';
import { useNavigate } from '~/lib/navigation';
import { CardProgramRepository } from '~/repositories/CardProgramRepository';
import { useNotificationStore } from '~/stores/Notification';
import { useSessionStore } from '~/stores/Session';
import { Flex, FormElement, FormLabel, FormText, Inner } from '~/styles';
import { formatDateTime, formatString } from '~/util';

import { CardProgramProps } from './CardProgram';

interface Params {
  id: string;
}

export const PageCardProgramInformation: React.FC = () => {
  const { addDangerNotification } = useNotificationStore();
  const navigate = useNavigate();
  const { id } = useParams<keyof Params>() as Params;
  const { cardProgram: outletCardProgram } = useOutletContext<CardProgramProps>();
  const { currentPlatform, isSandbox } = useSessionStore();

  const { data: cardProgram, error: cardProgramError } = useQuery({
    queryKey: ['card-program', id, currentPlatform?.id, isSandbox],
    queryFn: () => CardProgramRepository.get(id!),
    enabled: !!id && !outletCardProgram,
    initialData: outletCardProgram,
  });

  useEffect(() => {
    if (cardProgramError) {
      navigate(ROUTE.CARD_PROGRAMS);
      addDangerNotification({
        content: (cardProgramError as any).message || 'Failed to load card program',
      });
    }
  }, [cardProgramError, navigate, addDangerNotification]);

  if (!cardProgram) {
    return null;
  }

  return (
    <Inner p={0}>
      <Section headline="Information">
        <Inner px="var(--page-margin)" py={16}>
          <Flex flexDirection="column" gap={16}>
            <FormElement>
              <FormLabel>Name</FormLabel>
              <CopyInput value={cardProgram.description || '-'} />
            </FormElement>

            <FormElement>
              <FormLabel>Card Program ID</FormLabel>
              <CopyInput value={cardProgram.id} isMonospaced />
            </FormElement>

            <FormElement>
              <FormLabel>Network</FormLabel>
              <FormText>
                <Chip>{formatString(cardProgram.primaryCardScheme || '')}</Chip>
              </FormText>
            </FormElement>

            <FormElement>
              <FormLabel>Type</FormLabel>
              <FormText>
                <Chip>{formatString(cardProgram.cardProgramType || '')}</Chip>
              </FormText>
            </FormElement>

            <FormElement>
              <FormLabel>Status</FormLabel>
              <FormText>
                <Chip>{formatString(cardProgram.status || '')}</Chip>
              </FormText>
            </FormElement>

            <FormElement>
              <FormLabel>BIN</FormLabel>
              <CopyInput value={cardProgram.bin || '-'} isMonospaced />
            </FormElement>

            {cardProgram.createdAt && (
              <FormElement>
                <FormLabel>Created At</FormLabel>
                <CopyInput value={formatDateTime(cardProgram.createdAt, { timeZoneFormat: 'utc' })} />
              </FormElement>
            )}
          </Flex>
        </Inner>
      </Section>
    </Inner>
  );
};
